import React, {
	Fragment,
	forwardRef,
	memo,
	useCallback,
	useEffect,
	useImperativeHandle,
	useState
} from 'react';
import PropTypes from 'prop-types';
import { Form } from 'semantic-ui-react';

import useFormErrors from '../../utils/useFormErrors';

import useForm from '../../../../hooks/useForm';

import DynamicFormField from '../../DynamicFormField';

import styles from '../../document-modal.module.css';

const FileModule = forwardRef( ( {
	fileItems,
	instructions,
	moduleData,
	// setModuleErrors,
	validate
}, ref ) => {
	const [ uploads, setUploads ] = useState( [] );
	const [ downloads, setDownloads ] = useState( [] );

	const toFormData = useCallback( data => data.reduce( (
		acc,
		{
			questionId,
			value
		}
	) => {
		return {
			...acc,
			[ questionId ]: value
				? JSON.parse( value )
				: value
		};
	}, {} ), [] );

	const {
		formData,
		hasChanged,
		updateField
	} = useForm( toFormData( JSON.parse( moduleData.data ) ) );

	const toData = useCallback( data => JSON.parse( moduleData.data ).map( ( {
		questionId,
		...rest
	} ) => {
		const key = questionId;

		return {
			...rest,
			questionId,
			value: JSON.stringify( data[ key ] )
		};
	} ), [ moduleData.data ] );

	const {
		// formErrors,
		// setFormErrors,
		// validateFormErrors,
		handleValidation
	} = useFormErrors();

	const updateModuleData = useCallback( () => {
		const data = JSON.stringify( toData( formData ) );

		return hasChanged
			? { ...moduleData, data }
			: null;
	}, [
		formData,
		hasChanged,
		moduleData,
		toData
	] );

	useImperativeHandle( ref, () => ( { updateModuleData } ), [ updateModuleData ] );

	useEffect( () => {
		const uploads = [];
		const downloads = [];

		fileItems.forEach( item => {
			if ( item.config.download ) {
				downloads.push( item );
			} else {
				uploads.push( item );
			}
		} );

		setUploads( uploads );
		setDownloads( downloads );
	}, [ fileItems ] );

	const mapFileItems = useCallback( items => {
		return items.map( item => {
			const { label, description, id } = item;
			const [ , data ] = Object.entries( formData ).find( ( [ k ] ) => k === id ) ?? [];

			const errors = handleValidation( {
				data,
				label,
				validation: item.validation
			} );

			return (
				<Fragment key={ id }>
					<Form.Group style={ { marginBottom: '0' } }>
						<Form.Field>
							<label>{ label }</label>
							{ description
								? <p
									style={ {
										fontSize: '0.9rem',
										marginBottom: '1rem'
									} }
									dangerouslySetInnerHTML={ { __html: description } }
								/>
								: null
							}
						</Form.Field>
					</Form.Group>
					<Form.Group
						style={ { display: 'flex' } }
					>
						<DynamicFormField
							data={ data }
							errors={ errors }
							field={ item }
							forceValidation={ validate }
							updateField={ updateField }
						/>
					</Form.Group>
				</Fragment>
			);
		} );
	}, [
		formData,
		handleValidation,
		updateField,
		validate
	] );

	return (
		<>
			{ instructions
				? <div className={ styles.instructions }>
					<h3>Instructions</h3>
					<div dangerouslySetInnerHTML={ { __html: instructions } } />
				</div>
				: null
			}
			<div className={ styles.formContainer }>
				{ downloads.length === 0 && uploads.length === 0
					? 'There are no documents associated with this module.'
					: null
				}
				{ downloads.length
					? <>
						<h3>Downloads</h3>
						<Form>
							{ mapFileItems( downloads ) }
						</Form>
					</>
					: null
				}
				{ uploads.length
					? <>
						<h3>Uploads</h3>
						<Form>
							{ mapFileItems( uploads ) }
						</Form>
					</>
					: null
				}
			</div>
		</>
	);
} );

FileModule.displayName = 'FileModule';

FileModule.propTypes = {
	fileItems: PropTypes.array.isRequired,
	instructions: PropTypes.string,
	moduleData: PropTypes.object.isRequired,
	setModuleErrors: PropTypes.func,
	validate: PropTypes.bool
};

export default memo( FileModule );
